import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Description as DrawingIcon,
  Edit as RevisionIcon,
  CheckCircle as ApprovalIcon,
  Schedule as PendingIcon,
  Warning as OverdueIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as ReportIcon,
  Notifications as NotificationIcon,
  Person as PersonIcon,
  Business as ProjectIcon,
  Timeline as TimelineIcon,
  Speed as PerformanceIcon,
  Assignment as TaskIcon,
  CloudDownload as DownloadIcon,
  Visibility as ViewIcon,
  Comment as CommentIcon,
  Priority as PriorityIcon,
  Update as UpdateIcon,
  Group as TeamIcon,
  Build as ImplementIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './DesignDashboard.css';

const DesignDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalDrawings: 0,
      activeRevisions: 0,
      pendingApprovals: 0,
      completedApprovals: 0,
      overdueItems: 0,
      teamMembers: 0
    },
    trends: {
      drawingsProgress: [],
      revisionsOverTime: [],
      approvalTimes: [],
      categoryDistribution: []
    },
    recentActivity: [],
    alerts: [],
    topProjects: [],
    pendingTasks: [],
    teamPerformance: []
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, trendsRes, activityRes, alertsRes, projectsRes, tasksRes, teamRes] = await Promise.all([
        api.get('/designs/stats'),
        api.get('/designs/trends'),
        api.get('/designs/recent-activity'),
        api.get('/designs/alerts'),
        api.get('/designs/top-projects'),
        api.get('/designs/pending-tasks'),
        api.get('/designs/team-performance')
      ]);

      setDashboardData({
        stats: statsRes.data,
        trends: {
          drawingsProgress: trendsRes.data.data?.monthlyTrends || [],
          revisionsOverTime: trendsRes.data.data?.monthlyTrends || [],
          approvalTimes: trendsRes.data.data?.approvalTimeTrends || [],
          categoryDistribution: trendsRes.data.data?.categoryDistribution || []
        },
        recentActivity: activityRes.data.data || [],
        alerts: alertsRes.data.data || [],
        topProjects: projectsRes.data.data || [],
        pendingTasks: tasksRes.data.data || {},
        teamPerformance: teamRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'create-drawing':
        // Navigate to create drawing
        break;
      case 'create-revision':
        // Navigate to create revision
        break;
      case 'create-approval':
        // Navigate to create approval
        break;
      case 'view-reports':
        // Navigate to reports
        break;
      default:
        break;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      in_progress: 'warning',
      pending: 'default',
      overdue: 'error',
      approved: 'success',
      rejected: 'error',
      under_review: 'info'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[priority] || 'default';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading design dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Design Dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Overview of design activities, progress, and team performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ReportIcon />}
            onClick={() => handleQuickAction('view-reports')}
          >
            Reports
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleQuickAction('create-drawing')}
          >
            New Drawing
          </Button>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }} className="grid-section metrics-grid">
        <Grid item xs={12} sm={6} md={2}>
          <Card className="card-equal">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Drawings
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.stats.totalDrawings}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="success.main">
                      +12% this month
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <DrawingIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className="card-equal">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Revisions
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.stats.activeRevisions}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="warning" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="warning.main">
                      +5% this week
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <RevisionIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className="card-equal">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pending Approvals
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.stats.pendingApprovals}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <PendingIcon color="info" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="info.main">
                      Avg 3.2 days
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <ApprovalIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className="card-equal">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.stats.completedApprovals}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="success.main">
                      +18% this month
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                    <ApprovalIcon />
                  </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className="card-equal">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Overdue Items
                  </Typography>
                  <Typography variant="h4" color="error">
                    {dashboardData.stats.overdueItems}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <OverdueIcon color="error" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="error.main">
                      Needs attention
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                  <OverdueIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Team Members
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.stats.teamMembers}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TeamIcon color="primary" sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="primary.main">
                      Active today
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
                  <TeamIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts and Analytics */}
      <Grid container spacing={3} sx={{ mb: 3 }} className="grid-section">
        <Grid item xs={12} md={8}>
          <Card className="card-equal">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Design Progress Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dashboardData.trends.drawingsProgress}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="total" stackId="1" stroke="#8884d8" fill="#8884d8" />
                  <Area type="monotone" dataKey="approved" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
                  <Area type="monotone" dataKey="rejected" stackId="1" stroke="#ffc658" fill="#ffc658" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className="card-equal">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Category Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData.trends.categoryDistribution || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {dashboardData.trends.categoryDistribution && dashboardData.trends.categoryDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }} className="grid-section">
        <Grid item xs={12} md={6}>
          <Card className="card-equal">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Approval Timeline
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dashboardData.trends.approvalTimes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="avgApprovalTime" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className="card-equal">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Team Performance
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dashboardData.teamPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="userName" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="approvedDesigns" fill="#8884d8" />
                  <Bar dataKey="totalDesigns" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts and Recent Activity */}
      <Grid container spacing={3} sx={{ mb: 3 }} className="grid-section">
        <Grid item xs={12} md={4}>
          <Card className="card-equal">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  Alerts
                </Typography>
                <Badge badgeContent={Array.isArray(dashboardData.alerts) ? dashboardData.alerts.length : 0} color="error">
                  <NotificationIcon />
                </Badge>
              </Box>
              <List dense>
                {(dashboardData.alerts || []).slice(0, 5).map((alert, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        bgcolor: alert.type === 'overdue' ? 'error.main' : 
                                alert.type === 'warning' ? 'warning.main' : 'info.main',
                        width: 32, 
                        height: 32 
                      }}>
                        {alert.type === 'overdue' && <OverdueIcon sx={{ fontSize: 16 }} />}
                        {alert.type === 'warning' && <OverdueIcon sx={{ fontSize: 16 }} />}
                        {alert.type === 'info' && <NotificationIcon sx={{ fontSize: 16 }} />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {alert.message}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatTime(alert.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {dashboardData.alerts.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  No alerts at this time
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className="card-equal">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <List dense>
                {(dashboardData.recentActivity || []).slice(0, 6).map((activity, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {activity.type === 'drawing' && <DrawingIcon sx={{ fontSize: 16 }} />}
                        {activity.type === 'revision' && <RevisionIcon sx={{ fontSize: 16 }} />}
                        {activity.type === 'approval' && <ApprovalIcon sx={{ fontSize: 16 }} />}
                        {activity.type === 'comment' && <CommentIcon sx={{ fontSize: 16 }} />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {activity.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatTime(activity.timestamp)} • {activity.user}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card className="card-equal">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Tasks
              </Typography>
              <List dense>
                {(dashboardData.pendingTasks?.pendingTasks || []).slice(0, 6).map((task, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: getPriorityColor(task.priority) + '.main'
                      }}>
                        <TaskIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {task.title}
                          </Typography>
                          <Chip 
                            label={task.priority} 
                            size="small" 
                            color={getPriorityColor(task.priority)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {task.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Due: {formatDate(task.dueDate)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton size="small">
                      <ViewIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Projects */}
      <Grid container spacing={3} className="grid-section">
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Active Projects
              </Typography>
              <Grid container spacing={2}>
                {(dashboardData.topProjects || []).map((project, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper className="project-paper" sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                          <ProjectIcon />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {project.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {project.client}
                          </Typography>
                        </Box>
                        <Chip 
                          label={project.status} 
                          size="small" 
                          color={getStatusColor(project.status)}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Progress</Typography>
                          <Typography variant="body2">{project.progress}%</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={project.progress} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                      
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">Drawings</Typography>
                          <Typography variant="body2" fontWeight="medium">{project.drawings}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">Revisions</Typography>
                          <Typography variant="body2" fontWeight="medium">{project.revisions}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">Approvals</Typography>
                          <Typography variant="body2" fontWeight="medium">{project.approvals}</Typography>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">
                          Due: {formatDate(project.dueDate)}
                        </Typography>
                        <Box>
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                          <IconButton size="small">
                            <UpdateIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: 24, 
          p: 2, 
          borderRadius: 2,
          boxShadow: 3
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DrawingIcon />}
            onClick={() => handleQuickAction('create-drawing')}
          >
            New Drawing
          </Button>
          <Button
            size="small"
            startIcon={<RevisionIcon />}
            onClick={() => handleQuickAction('create-revision')}
          >
            New Revision
          </Button>
          <Button
            size="small"
            startIcon={<ApprovalIcon />}
            onClick={() => handleQuickAction('create-approval')}
          >
            New Approval
          </Button>
          <Divider sx={{ my: 1 }} />
          <Button
            size="small"
            startIcon={<ReportIcon />}
            onClick={() => handleQuickAction('view-reports')}
          >
            View Reports
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DesignDashboard;